import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { CalendarDays, Layers, ArrowRight, Plus } from "lucide-react";
import { toast } from "sonner";

const VINTAGE = "https://images.unsplash.com/photo-1529590003495-b2646e2718bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHx2aW50YWdlJTIwYm9vayUyMHN0YWNrfGVufDB8fHx8MTc4NzEzNzMxMHww&ixlib=rb-4.1.0&q=85";

export default function ReadingLists() {
  const [lists, setLists] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ liste_adi: "", aciklama: "", tur: "Tematik Listeler" });
  const load = () => api.get("/reading-lists").then((r) => setLists(r.data));
  useEffect(() => { load(); }, []);
  const clubs = lists.filter((l) => l.tur === "Aylık Okuma Kulübü");
  const thematic = lists.filter((l) => l.tur === "Tematik Listeler");

  const create = async () => {
    if (!form.liste_adi.trim()) { toast.error("Liste adı gerekli."); return; }
    try {
      await api.post("/reading-lists", { ...form, kitap_id_listesi: [] });
      setOpen(false); setForm({ liste_adi: "", aciklama: "", tur: "Tematik Listeler" });
      toast.success("Okuma listen oluşturuldu!"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const Card = ({ l }) => (
    <Link to={`/liste/${l.id}`} data-testid={`list-${l.id}`} className="group border border-line rounded-xl overflow-hidden bg-surface hover:shadow-md transition-shadow flex fade-up">
      <div className="w-28 shrink-0 bg-surface-alt relative">
        {l.kitaplar?.[0]?.kapak_resmi ? <img src={l.kitaplar[0].kapak_resmi} alt="" className="w-full h-full object-cover" /> :
          <img src={VINTAGE} alt="" className="w-full h-full object-cover opacity-80" />}
      </div>
      <div className="p-5 flex-1">
        <span className="text-xs uppercase tracking-wider text-terra">{l.tur}</span>
        <h3 className="font-serif text-2xl text-ink mt-1 group-hover:text-terra transition-colors">{l.liste_adi}</h3>
        <p className="text-sm text-ink-2 mt-1 line-clamp-2">{l.aciklama}</p>
        <p className="text-xs text-muted-foreground mt-2">{l.kitaplar?.length || 0} kitap · Detaya git <ArrowRight size={12} className="inline" /></p>
      </div>
    </Link>
  );

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-start justify-between fade-up">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-terra">Keşfet</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight mt-1">Okuma Listeleri</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-list-btn" className="bg-terra hover:bg-terra-hover text-white gap-1 rounded-full"><Plus size={16} /> Liste Oluştur</Button>
          </DialogTrigger>
          <DialogContent className="bg-surface">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Yeni Okuma Listesi</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Liste Adı</Label><Input data-testid="list-name" value={form.liste_adi} onChange={(e) => setForm({ ...form, liste_adi: e.target.value })} className="mt-1" placeholder="Örn: Stoacılığa Giriş" /></div>
              <div><Label>Açıklama</Label><Textarea data-testid="list-desc" value={form.aciklama} onChange={(e) => setForm({ ...form, aciklama: e.target.value })} className="mt-1" /></div>
              <div>
                <Label>Tür</Label>
                <select data-testid="list-type" value={form.tur} onChange={(e) => setForm({ ...form, tur: e.target.value })} className="mt-1 w-full h-10 rounded-md border border-line bg-surface px-3 text-sm">
                  <option value="Tematik Listeler">Tematik Liste</option>
                  <option value="Aylık Okuma Kulübü">Aylık Okuma Kulübü</option>
                </select>
              </div>
              <p className="text-xs text-muted-foreground">Listeyi oluşturduktan sonra detay sayfasından kitap ekleyebilirsin. 5 liste oluşturana Küratör rozeti verilir.</p>
            </div>
            <DialogFooter><Button onClick={create} data-testid="list-save" className="bg-terra hover:bg-terra-hover text-white">Oluştur</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <section className="mt-8">
        <h2 className="font-serif text-2xl text-ink mb-4 flex items-center gap-2"><CalendarDays className="text-gold" size={22} /> Aylık Okuma Kulübü</h2>
        <div className="grid md:grid-cols-2 gap-5">
          {clubs.length ? clubs.map((l) => <Card key={l.id} l={l} />) : <p className="text-muted-foreground text-sm">Henüz aktif kulüp yok.</p>}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-serif text-2xl text-ink mb-4 flex items-center gap-2"><Layers className="text-sage" size={22} /> Tematik Listeler</h2>
        <div className="grid md:grid-cols-2 gap-5">
          {thematic.length ? thematic.map((l) => <Card key={l.id} l={l} />) : <p className="text-muted-foreground text-sm">Henüz tematik liste yok.</p>}
        </div>
      </section>
    </div>
  );
}
